import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import InstagramChat from '../components/InstagramChat';
import { useAuth } from '../context/AuthContext';
import { useSocket } from '../context/SocketContext';
import { chatAPI } from '../api/api';

const childChatStoreKey = (userId) => `rp_child_chat_${userId}`;

export default function ChildChatPage() {
  const { user } = useAuth();
  const { socket, isConnected } = useSocket();
  const [messages, setMessages] = useState([]);
  const [parentName, setParentName] = useState('Parent');

  useEffect(() => {
    const key = childChatStoreKey(user?._id || user?.id || 'anon');
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        setMessages(JSON.parse(raw));
      }
    } catch (_) {
      // Ignore malformed local cache
    }
  }, [user]);

  useEffect(() => {
    const key = childChatStoreKey(user?._id || user?.id || 'anon');
    localStorage.setItem(key, JSON.stringify(messages));
  }, [messages, user]);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        const res = await chatAPI.getChildHistory(120);
        const history = res.data?.messages || [];
        setMessages((prev) => (history.length > 0 ? history : prev));
      } catch (err) {
        toast.error('Could not load chat history');
      }
    };

    loadHistory();
  }, []);

  useEffect(() => {
    if (!socket) return;

    const handleParentMessage = (data) => {
      setMessages((prev) => [
        ...prev,
        {
          fromChild: false,
          message: data?.message || '',
          timestamp: data?.timestamp || Date.now(),
        },
      ]);
      if (data?.parentName) setParentName(data.parentName);
    };

    socket.on('parent_chat_message', handleParentMessage);

    const handleCallRequest = (data) => {
      const from = data?.parentName || data?.childName || 'Other user';
      const callType = data?.callType === 'video' ? 'video' : 'audio';
      toast(`${from} sent a ${callType} call request`);
    };

    socket.on('chat_call_request', handleCallRequest);
    return () => {
      socket.off('parent_chat_message', handleParentMessage);
      socket.off('chat_call_request', handleCallRequest);
    };
  }, [socket]);

  const parentId = useMemo(() => user?.parentId || null, [user]);

  const sendMessage = (text) => {
    if (!socket || !isConnected || !parentId) {
      toast.error('Parent connection unavailable');
      return;
    }

    const payload = {
      parentId,
      message: text,
      timestamp: Date.now(),
    };

    socket.emit('child_chat_message', payload);
    setMessages((prev) => [
      ...prev,
      {
        fromChild: true,
        message: text,
        timestamp: payload.timestamp,
      },
    ]);
  };

  const sendCallRequest = (callType) => {
    if (!socket || !isConnected || !parentId) {
      toast.error('Parent connection unavailable');
      return;
    }

    socket.emit('chat_call_request', {
      parentId,
      callType,
    });
    toast.success(`${callType === 'video' ? 'Video' : 'Audio'} call request sent`);
  };

  return (
    <div style={{ height: 'calc(100vh - 140px)' }}>
      <InstagramChat
        messages={messages}
        onSendMessage={sendMessage}
        userName={user?.name}
        otherUserName={parentName}
        isParent={false}
        onAudioCall={() => sendCallRequest('audio')}
        onVideoCall={() => sendCallRequest('video')}
      />
    </div>
  );
}
